import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    Box,
    Checkbox,
    Button,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo, useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { queryWasteMeasurementsForDataset } from 'src/schema';
import LoadingIndicator from './LoadingIndicator';
import { getOneLink } from 'src/utils';
import { convertAndFilterMeasurements } from 'src/wasteConversion';
import type { Mass, Volume } from 'convert-units';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface Props {
    datasetId: string;
    eventId: string;
    open: boolean;
    onClose: () => void;
    displayUnit: string;
}

interface MaterialSummary {
    materialId: string;
    materialName: string;
    total: number;
}

interface CategorySummary {
    categoryId: string;
    categoryName: string;
    color: string;
    materials: MaterialSummary[];
    categoryTotal: number;
}

// category colors
const CATEGORY_COLORS: Record<string, string> = {
    Recycling: '#2c5282', // dark blue
    Landfill: '#e53e3e', // red
    Compost: '#d69e2e', // yellow/gold
    Other: '#718096', // gray
};

const EMPTY_ARRAY: never[] = [];

export default function DataSummaryModal(props: Props) {
    const { datasetId, open, onClose, displayUnit } = props;
    const { data, isLoading, error } = queryWasteMeasurementsForDataset(datasetId);
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [selectedMaterialIds, setSelectedMaterialIds] = useState<Set<string>>(new Set());

    const wasteMeasurements = data?.wasteMeasurements ?? EMPTY_ARRAY;

    // Convert all measurements to display unit (handles custom units and volume↔mass via density)
    const convertedMeasurements = useMemo(
        () =>
            convertAndFilterMeasurements(wasteMeasurements, {
                targetUnit: displayUnit as Volume | Mass,
                itemIds: [],
                datasetIds: [],
            }),
        [wasteMeasurements, displayUnit]
    );

    const categorySummaries = useMemo(() => {
        const categoryMap = new Map<string, CategorySummary>();

        convertedMeasurements.forEach(measurement => {
            const material = getOneLink(measurement.material);
            const category = getOneLink(material?.category);
            if (!material || !category || !measurement.quantity) return;

            const categoryId = category.id;
            const categoryName = category.name as string;
            const materialId = material.id;
            const materialName = material.name as string;
            const convertedQuantity = measurement.quantity as number;

            if (!categoryMap.has(categoryId)) {
                categoryMap.set(categoryId, {
                    categoryId,
                    categoryName,
                    color: CATEGORY_COLORS[categoryName] || '#718096',
                    materials: [],
                    categoryTotal: 0,
                });
            }
            const categorySummary = categoryMap.get(categoryId);
            if (!categorySummary) return;
            const existingMaterialSummary = categorySummary.materials.find(m => m.materialId === materialId);

            if (!existingMaterialSummary) {
                const newMaterialSummary: MaterialSummary = {
                    materialId,
                    materialName,
                    total: convertedQuantity,
                };
                categorySummary.materials.push(newMaterialSummary);
            } else {
                existingMaterialSummary.total += convertedQuantity;
            }

            categorySummary.categoryTotal += convertedQuantity;
        });

        const result: CategorySummary[] = [];
        for (const v of Array.from(categoryMap.values())) result.push(v);
        return result;
    }, [convertedMeasurements]);

    const materialFilterActive = selectedMaterialIds.size > 0;

    /** Categories and row totals for the table: when filtering, only categories with ≥1 selected material and totals from selection only. */
    const tableRows = useMemo(() => {
        if (!materialFilterActive) {
            return categorySummaries.map(cat => ({
                category: cat,
                rowTotal: cat.categoryTotal,
                singleMaterialLabel: cat.materials.length === 1 ? cat.materials[0].materialName : null,
            }));
        }
        return categorySummaries
            .map(cat => {
                const selected = cat.materials.filter(m => selectedMaterialIds.has(m.materialId));
                const rowTotal = selected.reduce((s, m) => s + m.total, 0);
                return {
                    category: cat,
                    rowTotal,
                    singleMaterialLabel: selected.length === 1 ? selected[0].materialName : null,
                };
            })
            .filter(row => row.rowTotal > 0);
    }, [categorySummaries, selectedMaterialIds, materialFilterActive]);

    const grandTotal = useMemo(() => {
        if (!materialFilterActive) {
            return categorySummaries.reduce((sum, cat) => sum + cat.categoryTotal, 0);
        }
        return categorySummaries.reduce(
            (sum, cat) => sum + cat.materials.filter(m => selectedMaterialIds.has(m.materialId)).reduce((s, m) => s + m.total, 0),
            0
        );
    }, [categorySummaries, selectedMaterialIds, materialFilterActive]);

    // Compute all material IDs for selection state
    const allMaterialIds = useMemo(() => {
        return new Set(categorySummaries.flatMap(cat => cat.materials.map(m => m.materialId)));
    }, [categorySummaries]);

    // Compute selection state for "Select All" checkbox
    const allMaterialsSelected = useMemo(() => {
        if (allMaterialIds.size === 0) return false;
        if (allMaterialIds.size !== selectedMaterialIds.size) return false;
        for (const id of Array.from(allMaterialIds)) {
            if (!selectedMaterialIds.has(id)) return false;
        }
        return true;
    }, [allMaterialIds, selectedMaterialIds]);

    const someMaterialsSelected = useMemo(() => {
        return selectedMaterialIds.size > 0 && selectedMaterialIds.size < allMaterialIds.size;
    }, [selectedMaterialIds, allMaterialIds]);

    const chartInstanceRef = useRef<Chart | null>(null);

    const canvasRef = useCallback(
        (canvas: HTMLCanvasElement | null) => {
            if (!canvas || categorySummaries.length === 0) return;

            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            // Helper function to adjust color brightness/saturation for material variants
            const adjustColor = (baseColor: string, index: number, total: number) => {
                // Parse HSL from hex color
                const hex = baseColor.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16) / 255;
                const g = parseInt(hex.substring(2, 4), 16) / 255;
                const b = parseInt(hex.substring(4, 6), 16) / 255;

                const max = Math.max(r, g, b);
                const min = Math.min(r, g, b);
                let h = 0,
                    s = 0,
                    l = (max + min) / 2;

                if (max !== min) {
                    const d = max - min;
                    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                    switch (max) {
                        case r:
                            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                            break;
                        case g:
                            h = ((b - r) / d + 2) / 6;
                            break;
                        case b:
                            h = ((r - g) / d + 4) / 6;
                            break;
                    }
                }

                // Adjust lightness for material variants
                const lightnessStep = total > 1 ? 15 / total : 0;
                const adjustedL = Math.min(Math.max(l * 100 - lightnessStep * index + lightnessStep * (total / 2), 25), 75);

                return `hsl(${h * 360}, ${s * 100}%, ${adjustedL}%)`;
            };

            // Build chart data with materials
            const labels: string[] = [];
            const data: number[] = [];
            const colors: string[] = [];

            // If no materials are selected, show category breakdowns
            if (selectedMaterialIds.size === 0) {
                categorySummaries.forEach(category => {
                    labels.push(category.categoryName);
                    data.push(category.categoryTotal);
                    colors.push(category.color);
                });
            } else {
                // Show selected materials individually
                categorySummaries.forEach(category => {
                    const visibleMaterials = category.materials.filter(m => selectedMaterialIds.has(m.materialId));

                    if (visibleMaterials.length > 0) {
                        visibleMaterials.forEach((material, index) => {
                            labels.push(material.materialName);
                            data.push(material.total);
                            colors.push(adjustColor(category.color, index, visibleMaterials.length));
                        });
                    }
                    // Skip categories with no selected materials
                });
            }

            chartInstanceRef.current = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels,
                    datasets: [
                        {
                            data,
                            backgroundColor: colors,
                            borderWidth: 1,
                            borderColor: '#fff',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                padding: 15,
                                font: { size: 11 },
                                boxWidth: 12,
                            },
                        },
                        title: {
                            display: true,
                            text: `Waste by ${selectedMaterialIds.size > 0 ? 'Material' : 'Category'} (${displayUnit})`,
                            font: { size: 14, weight: 'normal' },
                            padding: { bottom: 20 },
                        },
                        tooltip: {
                            callbacks: {
                                label: context => {
                                    const value = context.raw as number;
                                    const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
                                    const percentage = ((value / total) * 100).toFixed(1);
                                    return `${context.label}: ${value.toFixed(1)} ${displayUnit} (${percentage}%)`;
                                },
                            },
                        },
                        datalabels: {
                            formatter: (value, context) => {
                                const total = (context.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
                                const percentage = (value / total) * 100;
                                return percentage > 5 ? `${percentage.toFixed(1)}%` : '';
                            },
                            color: '#fff',
                            font: {
                                weight: 'bold',
                                size: 11,
                            },
                        },
                    },
                },
            });
        },
        [categorySummaries, displayUnit, selectedMaterialIds]
    );

    useEffect(() => {
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, []);

    if (isLoading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <LoadingIndicator message="Loading summary..." />
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Typography color="error">Error loading summary data</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '80vh' } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={600}>
                        Summary
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {/* Table Section */}
                <Box sx={{ px: 2, pt: 2, pb: 1, overflow: 'auto', flex: 1 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {tableRows.map(({ category, rowTotal, singleMaterialLabel }) => (
                                    <Fragment key={category.categoryId}>
                                        <TableRow
                                            sx={{
                                                '&:hover': { backgroundColor: 'action.hover' },
                                                transition: 'background-color 0.2s',
                                            }}
                                        >
                                            <TableCell sx={{ border: 0, py: 0.75 }}>
                                                <Box display="flex" alignItems="center">
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            backgroundColor: category.color,
                                                            mr: 1.5,
                                                        }}
                                                    />
                                                    <Typography fontWeight={600} fontSize="0.875rem">
                                                        {category.categoryName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ border: 0, py: 0.75 }}>
                                                {singleMaterialLabel ? (
                                                    <Typography fontSize="0.875rem">{singleMaterialLabel}</Typography>
                                                ) : (
                                                    <Typography fontSize="0.875rem" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ border: 0, py: 0.75 }}>
                                                <Typography fontWeight={600} fontSize="0.875rem">
                                                    {rowTotal.toFixed(1)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {selectedMaterialIds.size > 0 &&
                                            category.materials
                                                .filter(m => selectedMaterialIds.has(m.materialId))
                                                .map(material => (
                                                    <TableRow
                                                        key={`${category.categoryId}-${material.materialId}`}
                                                        sx={{
                                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                            '&:hover': { backgroundColor: 'action.hover' },
                                                        }}
                                                    >
                                                        <TableCell sx={{ border: 0, py: 0.5, pl: 5 }}>
                                                            <Box display="flex" alignItems="center">
                                                                <Box
                                                                    sx={{
                                                                        width: 5,
                                                                        height: 5,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: category.color,
                                                                        mr: 1.5,
                                                                        opacity: 0.6,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ border: 0, py: 0.5 }}>
                                                            <Typography variant="body2" fontSize="0.8rem">
                                                                {material.materialName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
                                                            <Typography variant="body2" fontSize="0.8rem">
                                                                {material.total.toFixed(1)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                    </Fragment>
                                ))}
                                <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                                    <TableCell colSpan={2} sx={{ border: 0, py: 1, fontWeight: 700, fontSize: '0.875rem' }}>
                                        Grand Total:
                                    </TableCell>
                                    <TableCell align="right" sx={{ border: 0, py: 1, fontWeight: 700, fontSize: '0.875rem' }}>
                                        {grandTotal.toFixed(1)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {/* Chart Section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.01)',
                        borderTop: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ maxWidth: 350, width: '100%' }}>
                        <canvas ref={canvasRef} />
                    </Box>
                </Box>

                {/* Footer Controls */}
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'nowrap',
                        borderTop: 1,
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                    }}
                >
                    <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                        {selectedMaterialIds.size === 0
                            ? 'No materials selected'
                            : `${selectedMaterialIds.size} of ${allMaterialIds.size} materials selected`}
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setSelectorOpen(true)}
                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 2 }}
                    >
                        Select Materials
                    </Button>
                </Box>
            </DialogContent>

            {/* Material Selector Dialog */}
            <Dialog open={selectorOpen} onClose={() => setSelectorOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ py: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontSize="1.1rem">
                            Select Materials
                        </Typography>
                        <IconButton onClick={() => setSelectorOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
                    <List sx={{ pt: 0, pb: 0 }} dense>
                        {categorySummaries.map(category => {
                            const categoryMaterialIds = category.materials.map(m => m.materialId);
                            const selectedCount = categoryMaterialIds.filter(id => selectedMaterialIds.has(id)).length;
                            const allSelected = selectedCount === categoryMaterialIds.length;
                            const someSelected = selectedCount > 0 && selectedCount < categoryMaterialIds.length;

                            const handleCategoryToggle = () => {
                                setSelectedMaterialIds(prev => {
                                    const newSet = new Set(prev);
                                    if (allSelected) {
                                        categoryMaterialIds.forEach(id => newSet.delete(id));
                                    } else {
                                        categoryMaterialIds.forEach(id => newSet.add(id));
                                    }
                                    return newSet;
                                });
                            };

                            return (
                                <Box key={category.categoryId}>
                                    <ListItemButton sx={{ pl: 2, py: 0.5, minHeight: 32 }} onClick={handleCategoryToggle}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <Checkbox edge="start" checked={allSelected} indeterminate={someSelected} size="small" />
                                        </ListItemIcon>
                                        <Box display="flex" alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: category.color,
                                                    mr: 1,
                                                }}
                                            />
                                            <Typography variant="subtitle2" fontWeight={600} fontSize="0.875rem">
                                                {category.categoryName}
                                            </Typography>
                                        </Box>
                                    </ListItemButton>
                                    {category.materials.map(material => (
                                        <ListItemButton
                                            key={material.materialId}
                                            dense
                                            sx={{ pl: 5, py: 0.25, minHeight: 36 }}
                                            onClick={() => {
                                                setSelectedMaterialIds(prev => {
                                                    const newSet = new Set(prev);
                                                    if (newSet.has(material.materialId)) {
                                                        newSet.delete(material.materialId);
                                                    } else {
                                                        newSet.add(material.materialId);
                                                    }
                                                    return newSet;
                                                });
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={selectedMaterialIds.has(material.materialId)}
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={material.materialName}
                                                secondary={`${material.total.toFixed(1)} ${displayUnit}`}
                                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </Box>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions sx={{ py: 1, px: 2, justifyContent: 'space-between' }}>
                    <Box display="flex" alignItems="center">
                        <Checkbox
                            checked={allMaterialsSelected}
                            indeterminate={someMaterialsSelected}
                            onChange={() => {
                                if (allMaterialsSelected) {
                                    setSelectedMaterialIds(new Set());
                                } else {
                                    setSelectedMaterialIds(new Set(allMaterialIds));
                                }
                            }}
                            size="small"
                        />
                        <Typography
                            variant="body2"
                            fontSize="0.875rem"
                            onClick={() => {
                                if (allMaterialsSelected) {
                                    setSelectedMaterialIds(new Set());
                                } else {
                                    setSelectedMaterialIds(new Set(allMaterialIds));
                                }
                            }}
                            sx={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            Select All
                        </Typography>
                    </Box>
                    <Button onClick={() => setSelectorOpen(false)}>Done</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
